import { BlogEditorRightToolBarActionMarkEnum } from '@doltech/utils/lib/constants';
import { pipe } from '@doltech/utils/lib/pipe';
import { noop } from 'lodash';
import * as React from 'react';
import { useDebounce } from 'react-use';
import { Editor, Range } from 'slate';
import { ReactEditor } from 'slate-react';

import { HeadingPlugin } from '../../slatePlugins/elements/heading/HeadingPlugin';
import { SoftBreakPlugin } from '../../slatePlugins/handlers/soft-break/SoftBreakPlugin';
import { useDolEditorStore } from '../DolEditor/dol-editor.store';
import { BorderShadingElementPlugin } from '../DolEditor/elements/border-shading/plugin';
import { DividerElementPlugin } from '../DolEditor/elements/divider/plugin';
import { HoverCardElementPlugin } from '../DolEditor/elements/hover-card/plugin';
import { BlogTablePlugin } from '../DolEditor/elements/table-blog/BlogTablePlugin';
import { ReadOnlyPlugin } from '../DolEditor/handlers/read-only/ReadOnlyPlugin';
import { BackgroundColorPlugin } from '../DolEditor/marks/background-color/BackgroundColorPlugin';
import { DisableEnterPlugin } from '../DolEditor/toolbars/disable-enter/DisableEnterPlugin';
import { useBlogEditorStore } from './blog-editor.store';
import { PluginPresetEnum } from './BlogEditor.types';
import { DEFAULTS_OPTIONS } from './editorDefaultOptions';
import { getAllExercisePlugins } from './plugin-presets/comboExercisePlugins';
import { getAllMediaObjectPlugins } from './plugin-presets/comboMediaObjectPlugins';
import { getAllSpecialObjectPlugins } from './plugin-presets/comboSpecialObjectPlugins';
import { getAllTextFormatPlugins } from './plugin-presets/comboTextFormatPlugins';
import { BulletPointPlugin } from './plugins/BulletPoint/BulletPointPlugin';
import { DivPlugin } from './plugins/DivPlugin';
import { EnterToScrollPlugin } from './plugins/enterToScrollPlugin';
import { PlaceholderPlugin } from './plugins/PlaceholderPlugin';
import { QuotePlugin } from './plugins/Quote/QuotePlugin';
import { patchFixInvalidNode, trimEmptyParagraph } from './shared-components/utils';

export const DEFAULT_BLOG_EDITOR_PLUGIN_PRESETS = [
  PluginPresetEnum.HEADING,
  PluginPresetEnum.TEXT_FORMAT,
  PluginPresetEnum.BLOCK_FORMAT,
  PluginPresetEnum.HTML_OBJECT,
  PluginPresetEnum.MEDIA_OBJECT,
  PluginPresetEnum.DIVIDER,
  PluginPresetEnum.EXERCISE_OBJECT,
  PluginPresetEnum.SPECIAL_OBJECT,
  PluginPresetEnum.CORE_PARAGRAPH_AT_END,
];

export interface UseBlogEditorPluginProps {
  editor: any;
  isReadOnly?: boolean;
  onBackWhenInComplete?: () => void;
  presets?: PluginPresetEnum[];
  extraPlugins?: any;
  overrideRightToolbar?: any;
  isEmailMode?: boolean;
}

export interface UseBlogEditorReadOnlyPluginProps {
  onBackWhenInComplete?: () => void;
  presets?: PluginPresetEnum[];
  extraPlugins?: any;
}

export const useBlogEditorPlugin = ({
  editor,
  isReadOnly = false,
  onBackWhenInComplete = noop,
  presets = DEFAULT_BLOG_EDITOR_PLUGIN_PRESETS,
  extraPlugins = [],
  overrideRightToolbar,
}: UseBlogEditorPluginProps) => {
  const dolEditorStore = useDolEditorStore();
  const blogEditorStore = useBlogEditorStore();

  const options = React.useMemo(() => {
    return { ...DEFAULTS_OPTIONS, isReadOnly };
  }, [isReadOnly]);

  const headingEnabled = React.useMemo(() => {
    let isSupportHeading = true;
    if (overrideRightToolbar && overrideRightToolbar?.length > 0) {
      const allActions = overrideRightToolbar.reduce((p, n) => p.concat(n.actions), []);
      isSupportHeading = allActions.some((action) =>
        [
          BlogEditorRightToolBarActionMarkEnum.H2,
          BlogEditorRightToolBarActionMarkEnum.H3,
          BlogEditorRightToolBarActionMarkEnum.H4,
        ].includes(action)
      );
    }
    return isSupportHeading;
  }, [overrideRightToolbar]);

  const plugins = React.useMemo(() => {
    const corePlugins = [
      // core
      DivPlugin({ headingEnabled }),
      DisableEnterPlugin(),
      EnterToScrollPlugin(),
      SoftBreakPlugin({
        rules: [
          {
            hotkey: 'shift+enter',
          },
        ],
      }),
    ];

    let exercisePlugins = [];
    if (presets.includes(PluginPresetEnum.EXERCISE_OBJECT)) {
      exercisePlugins = getAllExercisePlugins({ isReadOnly, onBackWhenInComplete });
    }

    const headingPlugins = [
      headingEnabled && presets.includes(PluginPresetEnum.HEADING) && HeadingPlugin(options), // h1, h2, h3, h4, h5
    ];

    let textFormatPlugins = [];
    if (presets.includes(PluginPresetEnum.TEXT_FORMAT)) {
      textFormatPlugins = getAllTextFormatPlugins({ options, isReadOnly });
    }

    let mediaObjectPlugins = [];
    if (presets.includes(PluginPresetEnum.MEDIA_OBJECT)) {
      mediaObjectPlugins = getAllMediaObjectPlugins({ isReadOnly });
    }

    let specialObjectPlugins = [];
    if (presets.includes(PluginPresetEnum.SPECIAL_OBJECT)) {
      specialObjectPlugins = getAllSpecialObjectPlugins({ isReadOnly });
    }

    const usedPlugins = [
      presets.includes(PluginPresetEnum.CORE_PLACEHOLDER_FOR_NODE) &&
        PlaceholderPlugin({ isReadOnly }),
      isReadOnly ? ReadOnlyPlugin() : undefined,

      ...textFormatPlugins,
      ...headingPlugins,

      // block format (node)
      presets.includes(PluginPresetEnum.BLOCK_FORMAT) && BorderShadingElementPlugin({ isReadOnly }),
      presets.includes(PluginPresetEnum.BLOCK_FORMAT) && BackgroundColorPlugin(), // node level - mostly use for border and shading
      presets.includes(PluginPresetEnum.BLOCK_FORMAT) && HoverCardElementPlugin({ isReadOnly }), // anchor link which have popup info

      // html object
      presets.includes(PluginPresetEnum.HTML_OBJECT) && BulletPointPlugin({ isReadOnly }),
      presets.includes(PluginPresetEnum.HTML_OBJECT) &&
        BlogTablePlugin({ isReadOnly, stickyHeaderOffset: 64 }),
      presets.includes(PluginPresetEnum.HTML_OBJECT) && QuotePlugin({ isReadOnly }),

      ...mediaObjectPlugins, // TODO: rehydrate failed

      // divider
      presets.includes(PluginPresetEnum.DIVIDER) && DividerElementPlugin(), // a divider which show "..."

      ...specialObjectPlugins, // TODO: rehydrate failed
      ...exercisePlugins,
      ...extraPlugins,
      ...corePlugins,
    ];

    return usedPlugins.filter(Boolean);
  }, [extraPlugins, headingEnabled, isReadOnly, onBackWhenInComplete, options, presets]);

  useDebounce(
    () => {
      if (isReadOnly) return;
      if (editor) {
        const { selection } = editor;
        const isFocused = ReactEditor.isFocused(editor);
        if (selection && isFocused) {
          blogEditorStore.selectState.disableAll(false);
          blogEditorStore.selectState.reset();
          const nodeEntries = Editor.nodes(editor, {
            at: editor?.selection,
          });

          const isTextSelection = Range.isExpanded(selection);

          blogEditorStore.selectState.updateIsTextSelection(isTextSelection);
          for (const [node, path] of nodeEntries) {
            blogEditorStore.selectState.markSelectionCheck({ node, path });
            blogEditorStore.selectState.cardLogicSelectionCheck({ node, path });
            blogEditorStore.selectState.tableSelectionCheck({ node, path });
            blogEditorStore.selectState.borderShadingSelectionCheck({ node });
            blogEditorStore.selectState.vocabCardCheck({ node });
          }
          blogEditorStore.selectState.doneCheckSelectState();
        } else {
          blogEditorStore.selectState.disableAll();
        }
      }
    },
    200,
    [dolEditorStore?.afterSelectionId]
  );

  const preprocessReadOnlyInitialValues = ({ initialValue }) => {
    return pipe(patchFixInvalidNode, trimEmptyParagraph)(initialValue);
  };

  const preprocessEditorInitialValues = ({ initialValue }) => {
    return patchFixInvalidNode(initialValue);
  };

  return {
    presets,
    plugins,
    preprocessReadOnlyInitialValues,
    preprocessEditorInitialValues,
  };
};

export const useTitleTemplateHeaderEditorPlugin = ({ isReadOnly = false }) => {
  return useBlogEditorPlugin({
    editor: null,
    isReadOnly,
    presets: [
      PluginPresetEnum.HEADING,
      PluginPresetEnum.TEXT_FORMAT,
      PluginPresetEnum.MEDIA_OBJECT,
      PluginPresetEnum.CORE_PLACEHOLDER_FOR_NODE,
    ],
  });
};

export const useDailyNewEditorPlugin = ({ editor, isReadOnly = false }) => {
  return useBlogEditorPlugin({
    editor,
    isReadOnly,
  });
};

export const useAssignmentPlugin = ({ isReadOnly = false, overrideRightToolbar = null }) => {
  return useBlogEditorPlugin({
    editor: null,
    isReadOnly,
    presets: [PluginPresetEnum.TEXT_FORMAT].filter(Boolean),
    overrideRightToolbar,
  });
};

export const useWritingAssignmentEditor = ({ editor, isReadOnly = false }) => {
  const { plugins, preprocessReadOnlyInitialValues } = useBlogEditorPlugin({
    editor,
    isReadOnly,
    presets: [],
    extraPlugins: [],
  });
  return {
    plugins,
    preprocessReadOnlyInitialValues,
  };
};

export const useMarkingWritingAssignmentWithoutCommentEditor = ({
  withoutEditorFormat = false,
  editor,
  isReadOnly = false,
}) => {
  const { presets, plugins, preprocessReadOnlyInitialValues } = useBlogEditorPlugin({
    editor,
    isReadOnly,
    presets: [PluginPresetEnum.TEXT_FORMAT],
    extraPlugins: [],
  });
  return {
    presets,
    plugins: withoutEditorFormat ? [] : plugins,
    preprocessReadOnlyInitialValues,
  };
};

export const useAssignentCommentMarkingEditor = ({ editor, isReadOnly = false }) => {
  const { presets, plugins, preprocessReadOnlyInitialValues } = useBlogEditorPlugin({
    editor,
    isReadOnly,
    presets: [
      PluginPresetEnum.TEXT_FORMAT,
      PluginPresetEnum.BLOCK_FORMAT,
      PluginPresetEnum.HTML_OBJECT,
      PluginPresetEnum.CORE_PARAGRAPH_AT_END,
      PluginPresetEnum.DIVIDER,
      PluginPresetEnum.HEADING,
    ],
    extraPlugins: [],
  });
  return {
    presets,
    plugins,
    preprocessReadOnlyInitialValues,
  };
};

export const useJuniorEntranceTestCommentMarkingEditor = ({ editor, isReadOnly = false }) => {
  const { presets, plugins, preprocessReadOnlyInitialValues } = useBlogEditorPlugin({
    editor,
    isReadOnly,
    presets: [
      PluginPresetEnum.TEXT_FORMAT,
      PluginPresetEnum.BLOCK_FORMAT,
      PluginPresetEnum.HTML_OBJECT,
      PluginPresetEnum.CORE_PARAGRAPH_AT_END,
      PluginPresetEnum.DIVIDER,
      PluginPresetEnum.HEADING,
    ],
    extraPlugins: [],
  });
  return {
    presets,
    plugins,
    preprocessReadOnlyInitialValues,
  };
};
